/**
 * Theme host — loads kind:theme packages and applies CSS variables to :root.
 * Themes are portable packages (like wallpapers/apps), not shell special-cases.
 */

import { diffThemeVars } from "./theme-vars.mjs";

export type ThemeModuleApi = {
  manifest?: {
    id?: string;
    name?: string;
    theme?: { cssVariables?: Record<string, string>; fonts?: string[] };
  };
  getCssVariables?: () => Record<string, string>;
  apply?: (root: HTMLElement) => void | Promise<void>;
  unapply?: () => void;
};

/**
 * Built-in fallback if package missing/fails (macOS glass baseline).
 *
 * This is the WHOLE `:root` token set from styles/os.css, not a hand-picked
 * subset, and the difference matters. Every apply is now a diff (see
 * `applyThemeVars`): keys the previous theme set and the next one does not are
 * removed from `:root` rather than left to contaminate it. Removing a token
 * drops it back to whatever `os.css` declares — so a theme that names only
 * eight variables lands on a complete, coherent baseline instead of a
 * half-recoloured one.
 *
 * It has to be the full set for a second reason: this map is what
 * `getActiveThemeVars` hands out and what gets cached to localStorage for the
 * pre-paint. A token missing here is a token no consumer can read before the
 * stylesheet parses. `ctx.charts` resolves its palette from
 * `--os-window-body` at exactly that moment.
 *
 * KEEP IN SYNC with the `:root` block in styles/os.css. The three the shell
 * writes inline on <html> — `--os-screen-w`, `--os-screen-h`, `--os-ui-scale`
 * — are deliberately absent; see HOST_OWNED_VARS in theme-vars.mjs.
 */
export const FALLBACK_THEME_VARS: Record<string, string> = {
  "--os-bg": "#05060c",
  /**
   * The opaque document surface inside a window. Themeable — a light theme has
   * to be able to lighten it, or it ships light chrome around a black body.
   * `ctx.charts` reads this token to pick its palette.
   */
  "--os-window-body": "#0e1017",
  "--os-glass-bg": "rgba(32, 34, 42, 0.52)",
  "--os-glass-bg-strong": "rgba(28, 30, 38, 0.72)",
  /* The step where glass stops being see-through: menus, context menus and
     modal cards, which have to hold a list of hit targets over any wallpaper.
     A light theme must move this with the other two or its menus go dark. */
  "--os-glass-bg-heavy": "rgba(28, 30, 38, 0.94)",
  "--os-glass-border": "rgba(255, 255, 255, 0.14)",
  "--os-glass-highlight": "rgba(255, 255, 255, 0.22)",
  "--os-text": "rgba(255, 255, 255, 0.94)",
  "--os-text-dim": "rgba(255, 255, 255, 0.55)",
  "--os-text-faint": "rgba(255, 255, 255, 0.32)",
  "--os-accent": "#0a84ff",
  "--os-accent-soft": "rgba(10, 132, 255, 0.28)",
  /* The accent lifted, for the top stop of a gradient on an accent fill.
     A color-mix rather than a hex so it follows whatever a theme sets
     --os-accent to; a theme only overrides it to change the *amount* of lift,
     or to darken instead of lighten under a pale accent. */
  "--os-accent-hi": "color-mix(in srgb, #fff 14%, var(--os-accent))",
  /* What is read ON an accent fill. A theme that picks a light accent MUST
     move this or every primary button in the OS gets white-on-pale — which is
     precisely why it is a token and not the `color:#fff` it replaced. */
  "--os-text-on-accent": "#fff",
  /* A link in body copy — the accent lifted to reading weight. Same split as
     the two reds: the colour you click is not the colour you read. */
  "--os-link": "#6cb6ff",
  "--os-menubar-bg": "rgba(18, 20, 28, 0.42)",
  "--os-dock-bg": "rgba(22, 24, 32, 0.48)",
  "--os-radius-window": "12px",
  "--os-radius-dock": "22px",
  "--os-font-ui":
    '"SF Pro Display", "SF Pro Text", "Inter", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
  "--os-font-mono": '"SF Mono", "JetBrains Mono", ui-monospace, Menlo, monospace',
  "--os-danger": "#ff453a",
  /* The red for text, as opposed to the red that fills a destructive control.
     A light theme has to move this separately: backing a red off in saturation
     is what makes it readable on dark, and the same move makes it disappear on
     white. */
  "--os-text-danger": "#ff6961",
  "--os-warn": "#ffd60a",
  /* Amber — "this drifted, look at it", between warn and danger. Shares its
     value with --os-tint-orange the way --os-accent shares one with
     --os-tint-blue: same colour, different job, and a theme may well move one
     without the other. */
  "--os-caution": "#ff9f0a",
  /* Amber for text. #ff9f0a is a fill colour; at 11–12px its near-zero blue
     channel shimmers, which is why three modules each hand-picked their own
     readable amber before this existed. A light theme moves it the opposite
     way it moves --os-text-danger — lifting a hue for dark makes it vanish on
     white. */
  "--os-text-caution": "#ffcf8a",
  "--os-success": "#30d158",
  /* Older name for --os-success, still selected on across os.css. Aliased so a
     theme that sets only --os-success recolours both. */
  "--os-ok": "var(--os-success)",
  /* Green for text, the counterpart --os-text-danger already had. Four modules
     invented four greens for this one idea before it had a name. */
  "--os-text-success": "#5cd97f",
  "--os-shadow-window": "var(--os-elev-3)",
  "--os-shadow-dock": "var(--os-elev-2)",
  "--os-menubar-h": "28px",
  "--os-dock-pad": "18px",

  /* Scrollbars — OS chrome. */
  "--os-scroll-size": "8px",
  "--os-scroll-size-hover": "10px",
  /* Aliases the pill, same as :root does — a theme that squares every pill
     squares the thumbs with it, and this name is still there to opt out. */
  "--os-scroll-radius": "var(--os-radius-full)",
  "--os-scroll-track": "transparent",
  "--os-scroll-thumb": "rgba(255, 255, 255, 0.18)",
  "--os-scroll-thumb-hover": "rgba(255, 255, 255, 0.32)",
  "--os-scroll-thumb-active": "rgba(255, 255, 255, 0.42)",

  /* Spacing. -3-5 and -4-5 are half-steps, not new steps: 10 and 14 are the
     densest-used gaps in module CSS and the numbered scale skipped both. */
  "--os-space-1": "4px",
  "--os-space-2": "6px",
  "--os-space-3": "8px",
  "--os-space-3-5": "10px",
  "--os-space-4": "12px",
  "--os-space-4-5": "14px",
  "--os-space-5": "16px",
  "--os-space-6": "24px",

  /* Radius scale. --os-radius-window/-dock above are identity, not scale. */
  "--os-radius-sm": "6px",
  "--os-radius-md": "10px",
  "--os-radius-lg": "14px",
  "--os-radius-xl": "18px",
  /* The pill. A shape, not a step — "round the ends off whatever height this
     is" — which is why it does not sort between xl and a larger number. */
  "--os-radius-full": "999px",

  /* Type. --os-text-14/-16 are infill: the t-shirt names are API, so the two
     missing sizes could only be added, never inserted by renumbering. */
  "--os-text-xs": "10px",
  "--os-text-sm": "11px",
  "--os-text-base": "12px",
  "--os-text-md": "13px",
  "--os-text-14": "14px",
  "--os-text-lg": "15px",
  "--os-text-16": "16px",
  "--os-text-xl": "18px",

  /* Elevation. --os-shadow-window/-dock resolve through these.
     --os-elev-card is the resting height at card size, not a fourth height:
     elev-1 is scaled for a knob and reads flat under a 360px card. */
  "--os-elev-1": "0 2px 8px rgba(0, 0, 0, 0.28)",
  "--os-elev-card": "0 8px 24px rgba(0, 0, 0, 0.25)",
  "--os-elev-2": "0 10px 30px rgba(0, 0, 0, 0.38)",
  "--os-elev-3": "0 24px 60px rgba(0, 0, 0, 0.55)",

  /* Motion. A theme can slow the OS down or flatten it to 0ms. */
  "--os-dur-fast": "120ms",
  "--os-dur-base": "160ms",
  "--os-dur-slow": "240ms",
  "--os-ease-out": "cubic-bezier(0.22, 1, 0.36, 1)",
  "--os-ease-spring": "cubic-bezier(0.34, 1.4, 0.64, 1)",

  /* Backdrop blur. A flat theme sets these to 0 and stops paying for it. */
  "--os-blur-sm": "6px",
  "--os-blur-md": "28px",
  "--os-blur-lg": "40px",

  /* Borders and fills — the chrome family. These are white alphas, so a light
     theme MUST override them or its chrome washes out to nothing. */
  "--os-border-subtle": "rgba(255, 255, 255, 0.08)",
  "--os-border-default": "rgba(255, 255, 255, 0.14)",
  "--os-border-strong": "rgba(255, 255, 255, 0.22)",
  "--os-surface-1": "rgba(255, 255, 255, 0.03)",
  "--os-surface-2": "rgba(255, 255, 255, 0.06)",
  "--os-surface-3": "rgba(255, 255, 255, 0.1)",
  "--os-hover": "rgba(255, 255, 255, 0.08)",
  "--os-active": "rgba(255, 255, 255, 0.14)",
  /* The opaque band — the only surface in the set that is not a tint, and the
     only thing that can back a sticky header. A light theme MUST override it:
     left dark it puts a near-black strip across the top of every table. */
  "--os-surface-solid": "rgba(24, 26, 34, 0.96)",

  /* Recessed surfaces: 1 is inset from its parent, 2 is a hole in it. */
  "--os-well-1": "rgba(0, 0, 0, 0.12)",
  "--os-well-2": "rgba(0, 0, 0, 0.28)",

  /* The backdrop, not a third well: a scrim sits OVER the UI and pushes it
     back (behind a modal, under a caption on artwork) rather than being inset
     into it. A light theme still wants this dark — a scrim is a shadow over
     the world, not a surface — but may want it shallower. */
  "--os-scrim": "rgba(0, 0, 0, 0.45)",

  /* Window controls — the traffic lights. A theme that squares the window
     corners reaches these too, which is the whole point of tokenising them. */
  "--os-ctl-close": "#ff5f57",
  "--os-ctl-min": "#febc2e",
  "--os-ctl-max": "#28c840",
  "--os-ctl-inactive": "rgba(255, 255, 255, 0.28)",

  /* Dock app tints. */
  "--os-tint-blue": "#0a84ff",
  "--os-tint-purple": "#bf5af2",
  "--os-tint-gray": "#8e8e93",
  "--os-tint-orange": "#ff9f0a",
  "--os-tint-teal": "#3fc7bd",
  "--os-tint-green": "#30d158",

  /* Widgets, and the two fills that only exist because a blur was dropped. */
  "--os-widget-radius": "18px",
  "--os-widget-blur": "18px",
  "--os-widget-tint": "rgba(16, 20, 30, 0.52)",
  "--os-widget-tint-solid": "rgba(22, 26, 36, 0.96)",
  "--os-window-drag-bg": "rgba(28, 30, 38, 0.98)",

  "--os-grid-step": "104px",
  "--os-border": "rgba(255, 255, 255, 0.09)",
  "--os-font": 'system-ui, -apple-system, "Segoe UI", sans-serif',
  "--os-chart-tip-bg": "rgba(24, 26, 34, 0.96)",
  "--os-mobile-nav-h": "64px",

  /* Code highlighting — GitHub-dark, the values ctx.ui's editor already used
     as inline fallbacks. Here so a theme switch restores them: without an
     entry a theme that recolours the syntax palette would leave it recoloured
     for every theme after it, since the diff can only remove what it knows. */
  "--os-code-fg": "#d7dde8",
  "--os-code-kw": "#ff7b72",
  "--os-code-str": "#a5d6ff",
  "--os-code-com": "#8b949e",
  "--os-code-num": "#79c0ff",
};

let activeVars: Record<string, string> = { ...FALLBACK_THEME_VARS };
/**
 * The keys written onto `:root` by the last apply. Without this there is no
 * way to know what to take OFF — `setProperty` is not self-cleaning, so
 * before it existed every variable any theme had ever set stayed on the
 * element for the rest of the session.
 */
let appliedKeys: string[] = [];
let unapplyPrev: (() => void) | undefined;
let loadSeq = 0;
let onApplied: (() => void) | undefined;
const appliedSubs = new Set<() => void>();

export function setThemeAppliedHandler(fn: (() => void) | undefined) {
  onApplied = fn;
}

/**
 * Subscribe to "a theme finished applying". Anything that caches a resolved
 * token — `ctx.charts` resolves its palette from `--os-window-body` — must
 * re-read here, otherwise it keeps dark colours on a light theme.
 * Returns an unsubscribe. Multi-subscriber, unlike setThemeAppliedHandler,
 * which is the shell's single internal slot.
 */
export function onThemeApplied(fn: () => void): () => void {
  appliedSubs.add(fn);
  return () => appliedSubs.delete(fn);
}

function emitThemeApplied() {
  try {
    onApplied?.();
  } catch {
    /* */
  }
  for (const fn of Array.from(appliedSubs)) {
    try {
      fn();
    } catch {
      /* a bad subscriber must not stop the rest from repainting */
    }
  }
}

export function getActiveThemeVars(): Record<string, string> {
  let scale = "0.9";
  try {
    scale = localStorage.getItem("arclog.uiScale") || "0.9";
  } catch {
    /* */
  }
  return { ...activeVars, "--os-ui-scale": scale };
}

/**
 * Write a theme onto `:root`, and — the half that was missing — take the
 * previous one off. `setProperty` never removes anything, so an apply that
 * only ever sets leaves every token any earlier theme touched sitting on the
 * element, silently colouring the next theme. The diff lives in
 * theme-vars.mjs so the rule is testable without a DOM.
 */
export function applyThemeVars(vars: Record<string, string>) {
  const root = document.documentElement;
  const next = { ...FALLBACK_THEME_VARS, ...vars };
  const { set, remove, keys } = diffThemeVars(appliedKeys, next);
  activeVars = set;
  appliedKeys = keys;
  // Remove first: a key moving out of the theme should never be briefly
  // present twice in the same frame.
  for (const k of remove) root.style.removeProperty(k);
  for (const [k, v] of Object.entries(set)) root.style.setProperty(k, v);
  if (set["--os-bg"]) document.body.style.background = set["--os-bg"];
  if (set["--os-font-ui"]) document.body.style.fontFamily = set["--os-font-ui"];
}

async function loadThemeModule(id: string): Promise<ThemeModuleApi | null> {
  try {
    const url = `/modules/${encodeURIComponent(id)}/index.js?t=${Date.now()}`;
    const mod = (await import(/* @vite-ignore */ url)) as {
      default?: ThemeModuleApi;
    } & ThemeModuleApi;
    return (mod.default || mod) as ThemeModuleApi;
  } catch {
    return null;
  }
}

/** Resolve + apply a theme package by id (portable kind:theme ESM). */
export async function activateTheme(id: string): Promise<Record<string, string>> {
  const seq = ++loadSeq;
  const tid = (id || "theme.macos-glass").trim() || "theme.macos-glass";

  try {
    unapplyPrev?.();
  } catch {
    /* */
  }
  unapplyPrev = undefined;

  let vars: Record<string, string> = { ...FALLBACK_THEME_VARS };
  const api = await loadThemeModule(tid);
  if (seq !== loadSeq) return getActiveThemeVars();

  if (api) {
    try {
      const fromFn = api.getCssVariables?.() || {};
      const fromMan = api.manifest?.theme?.cssVariables || {};
      vars = { ...FALLBACK_THEME_VARS, ...fromMan, ...fromFn };
      if (api.apply) {
        await Promise.resolve(api.apply(document.documentElement));
        unapplyPrev = () => api.unapply?.();
      }
    } catch (e) {
      console.warn("[theme] package apply failed, using fallback", tid, e);
      vars = { ...FALLBACK_THEME_VARS };
    }
  } else if (tid !== "theme.macos-glass") {
    console.warn("[theme] module not found", tid);
  }

  applyThemeVars(vars);
  try {
    localStorage.setItem("arclog.theme", tid);
    localStorage.setItem("arclog.theme.vars", JSON.stringify(vars));
  } catch {
    /* */
  }
  emitThemeApplied();
  return getActiveThemeVars();
}

/** Sync paint from cache before packages finish loading. */
export function paintCachedTheme() {
  try {
    const raw = localStorage.getItem("arclog.theme.vars");
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, string>;
      if (parsed && typeof parsed === "object") {
        applyThemeVars(parsed);
        return;
      }
    }
  } catch {
    /* */
  }
  applyThemeVars(FALLBACK_THEME_VARS);
}
